import { FC } from 'react';
import { Box, Grid, Skeleton, Svg, SvgBubble, Text } from '@hopskipdrive/react-components';
import styled from 'styled-components';

import { colorTokens, variables } from '../../../../../shared/components/variables';
import { RideSummary } from '../../../../types/rideOverview';

interface SummaryOverviewProps {
  isFetching: boolean;
  summaryDetails?: RideSummary;
}

export const SummaryOverview: FC<SummaryOverviewProps> = ({ isFetching, summaryDetails }) => {
  return (
    <SummaryContainer container>
      <Grid item xs={12} lg={4}>
        <SummaryOverviewItem
          isFetching={isFetching}
          value={summaryDetails?.estimatedRideTime || '--'}
          label="Estimated Ride Time"
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <SummaryOverviewItem
          isFetching={isFetching}
          value={summaryDetails?.actualRideTime || '--'}
          label="Actual Ride Time"
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <SummaryOverviewItem isFetching={isFetching} value={summaryDetails?.totalDistance} label="Total distance" />
      </Grid>

      <Grid item xs={12} lg={4}>
        <SummaryOverviewItem
          isFetching={isFetching}
          value={summaryDetails?.minutesDifference || '--'}
          label="Than Expected"
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <SummaryOverviewItem isFetching={isFetching} value={summaryDetails?.billableMiles} label="Billable Miles" />
      </Grid>
      <Grid item xs={12} lg={4}>
        <SummaryOverviewItem isFetching={isFetching} value={summaryDetails?.fareMiles} label="CareDriver Fare Miles" />
      </Grid>
      <Grid item xs={12} lg={4}>
        <SummaryItemContainer>
          {isFetching && (
            <Skeleton data-testid="summary-overview-item-skeleton" variant="text" width={90} height={26} />
          )}
          {!isFetching && <Text variant="body1">{summaryDetails?.protectedEarnings || '--'}</Text>}
          <Box display="flex" gap="4px">
            <Text variant="body1" textColor={'secondaryText'}>
              {'Protected Earnings'}
            </Text>
            <SvgBubble
              onClick={() => window.open(summaryDetails?.protectedEarningsHelperLink, '_blank')}
              style={{ background: colorTokens.iconButton.default }}
            >
              <Svg icon="question" style={{ color: colorTokens.primary.contrast }} />
            </SvgBubble>
          </Box>
        </SummaryItemContainer>
      </Grid>
    </SummaryContainer>
  );
};

interface SummaryOverviewItemProps {
  value: string | number | undefined;
  label: string | undefined;
  labelColor?: string | undefined;
  isFetching?: boolean;
  valueClassName?: string;
}

const SummaryOverviewItem: React.FC<SummaryOverviewItemProps> = ({ value, label, labelColor, isFetching }) => {
  return (
    <SummaryItemContainer>
      {isFetching && <Skeleton data-testid="summary-overview-item-skeleton" variant="text" width={90} height={26} />}
      {!isFetching && (
        <Text className="value" variant="body1">
          {value}
        </Text>
      )}
      <Text className="label" variant="body1" textColor={labelColor || 'secondaryText'}>
        {label}
      </Text>
    </SummaryItemContainer>
  );
};

const SummaryContainer = styled(Grid)`
  display: flex;
  padding: 16px;
  align-items: flex-start;
  align-content: flex-start;
  row-gap: 13px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const SummaryItemContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px 16.561px;
  border-right: 1.38px solid ${colorTokens.border.main};
  border-radius: 0;
  gap: 5.52px;

  .value {
    font-size: 19.321px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
  .label {
    font-size: 17.941px;
    font-style: normal;
    font-weight: 400;
    line-height: 27.602px;
  }

  ${variables.mediaQuery.lessThan('desktop')`   
      border: none;
      flex-direction: row-reverse;
      justify-content: space-between;

      .value {
        margin-right: 64px;
        width: 30%;
      }
  `}
`;
